import { useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight, ArrowUpRight } from "lucide-react";
import { INSTALL_GALLERY } from "./data";
import { Reveal, scrollToId } from "./motion";

const CATS = ["All", "Residential", "Commercial"];

export const InstallGallery = () => {
  const [open, setOpen] = useState(null);
  const [cat, setCat] = useState("All");
  const photos = cat === "All" ? INSTALL_GALLERY : INSTALL_GALLERY.filter((g) => g.cat === cat);

  useEffect(() => {
    if (open === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(null);
      if (e.key === "ArrowRight") setOpen((i) => (i + 1) % photos.length);
      if (e.key === "ArrowLeft") setOpen((i) => (i - 1 + photos.length) % photos.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, photos.length]);

  return (
    <section id="installs" data-testid="install-gallery" className="relative py-28 lg:py-40 bg-[#050505] border-t border-[#1c1c1c]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16">
          <div>
            <Reveal><p className="text-xs tracking-[0.3em] uppercase text-[#fcc41b] font-display font-medium mb-5">Recent Installations</p></Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-display font-semibold tracking-tighter leading-[0.95] text-white" style={{ fontSize: "clamp(2.25rem, 5.5vw, 4.5rem)" }}>
                On real Australian<br />roofs.
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <p className="text-zinc-400 max-w-sm text-base leading-relaxed">
              Tile, terracotta or Colorbond — from family homes to warehouses. Fit it yourself with a straightforward DIY install, or let us connect you with our national installer network.
            </p>
          </Reveal>
        </div>

        <div className="flex flex-wrap gap-2 mb-10">
          {CATS.map((c) => (
            <button
              key={c}
              onClick={() => { setCat(c); setOpen(null); }}
              data-testid={`gallery-filter-${c.toLowerCase()}`}
              className={`font-mono-eng text-xs tracking-wider rounded-full px-5 py-2.5 border transition-colors duration-300 ${
                cat === c
                  ? "bg-[#fcc41b] text-black border-[#fcc41b] font-bold"
                  : "text-zinc-400 border-[#333] hover:border-[#fcc41b]/50 hover:text-white"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {photos.map((g, i) => (
            <Reveal key={g.src} delay={i * 0.06}>
              <button
                onClick={() => setOpen(i)}
                data-testid={`install-photo-${i}`}
                className="group relative w-full h-72 lg:h-80 rounded-3xl overflow-hidden border border-[#262626] hover:border-[#fcc41b]/40 transition-colors duration-500 text-left"
              >
                <img src={g.src} alt={g.caption} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#010101]/90 via-transparent to-transparent" />
                <span className="absolute top-5 left-5 font-mono-eng text-[10px] tracking-wider uppercase text-black bg-[#fcc41b] rounded-full px-3 py-1">{g.cat}</span>
                <span className="absolute bottom-5 left-5 right-5 font-mono-eng text-[11px] tracking-wider text-zinc-200">{g.caption}</span>
              </button>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <div className="mt-12 flex justify-center">
            <button
              onClick={() => scrollToId("quote")}
              data-testid="gallery-quote-cta"
              className="group inline-flex items-center gap-3 rounded-full bg-[#fcc41b] text-black font-display font-semibold px-8 py-4 hover:bg-[#e5ae13] active:scale-95 transition-[background-color,transform] duration-300"
            >
              Want yours installed? Request a quote
              <ArrowUpRight size={20} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
            </button>
          </div>
        </Reveal>
      </div>

      {open !== null && (
        <div data-testid="gallery-lightbox" className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4 lg:p-12" onClick={() => setOpen(null)}>
          <button data-testid="lightbox-close" aria-label="Close" className="absolute top-6 right-6 text-zinc-400 hover:text-white transition-colors duration-200" onClick={() => setOpen(null)}>
            <X size={28} />
          </button>
          <button
            data-testid="lightbox-prev"
            aria-label="Previous photo"
            className="absolute left-4 lg:left-8 text-zinc-400 hover:text-[#fcc41b] transition-colors duration-200"
            onClick={(e) => { e.stopPropagation(); setOpen((open - 1 + photos.length) % photos.length); }}
          >
            <ChevronLeft size={36} />
          </button>
          <figure className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <img src={photos[open].src} alt={photos[open].caption} className="w-full max-h-[78vh] object-contain rounded-2xl" />
            <figcaption className="mt-4 text-center font-mono-eng text-xs tracking-wider text-zinc-400">{photos[open].caption}</figcaption>
          </figure>
          <button
            data-testid="lightbox-next"
            aria-label="Next photo"
            className="absolute right-4 lg:right-8 text-zinc-400 hover:text-[#fcc41b] transition-colors duration-200"
            onClick={(e) => { e.stopPropagation(); setOpen((open + 1) % photos.length); }}
          >
            <ChevronRight size={36} />
          </button>
        </div>
      )}
    </section>
  );
};
